"use client";

import { useEffect, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Download, Check, Loader2 } from "lucide-react";
import {
  getAvailableCouponsForProduct,
  claimCoupon,
  type AvailableCoupon,
} from "@/app/actions/coupons";
import { getLocalizedCouponName } from "@/lib/utils/coupon-locale";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  areRatesLoaded,
  formatCouponAmount,
} from "@/lib/utils/coupon-calc";
import { convertCurrency, roundForCurrency } from "@/lib/utils/currency";
import { useCurrency } from "@/contexts/CurrencyContext";

interface ProductCouponBannerProps {
  productId: string;
  /** SSR에서 미리 조회된 쿠폰 (있으면 client fetch 생략) */
  initialCoupons?: AvailableCoupon[];
  onAuthRequired?: () => void;
}

/**
 * 메가커피 스타일 쿠폰 받기 배너 (상품 상세 페이지 소개 섹션 최상단)
 *
 * - 그라디언트 배경 + 티켓 모양 클립
 * - "쿠폰 받기" 버튼 → claim_coupon 호출
 * - 수령 완료 시 "수령 완료" 상태 전환
 * - 여러 쿠폰이면 세로 나열 (간격 있음)
 * - 사용 가능한 쿠폰 없으면 렌더 안 됨
 */
export function ProductCouponBanner({
  productId,
  initialCoupons,
  onAuthRequired,
}: ProductCouponBannerProps) {
  const t = useTranslations("coupon.banner");
  const locale = useLocale();
  // 사용자 표시 통화로 환전해서 표시 (상품 가격과 동일한 통화)
  // isHydrated가 false인 동안에는 SSR과 동일하게 native 통화로 표시 (hydration mismatch 방지).
  // useExchangeRates가 useState 초기값에서 localStorage 캐시를 동기적으로 읽기 때문에,
  // 클라이언트 첫 렌더에서 rates가 이미 채워져 서버(빈 객체)와 결과가 달라질 수 있음.
  const { selectedCurrency, rates, isHydrated } = useCurrency();
  const ratesReady = isHydrated && areRatesLoaded(rates);

  const [coupons, setCoupons] = useState<AvailableCoupon[]>(
    initialCoupons ?? [],
  );
  const [loading, setLoading] = useState(!initialCoupons);
  const [claiming, setClaiming] = useState<string | null>(null);
  // claimed 여부는 RPC 응답에 포함하지 않음.
  // "받기" 버튼을 눌렀을 때 claim_coupon의 응답(성공 or ALREADY_CLAIMED)으로 결정
  const [claimed, setClaimed] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    // SSR(RPC)에서 prefetch된 경우 fetch 생략
    if (initialCoupons) return;
    getAvailableCouponsForProduct(productId)
      .then((data) => setCoupons(data))
      .finally(() => setLoading(false));
  }, [productId, initialCoupons]);

  const handleClaim = async (coupon: AvailableCoupon) => {
    setClaiming(coupon.id);
    try {
      const result = await claimCoupon(coupon.id);

      if (!result.success) {
        if (result.error === "AUTH_REQUIRED") {
          onAuthRequired?.();
          return;
        }
        // 이미 받은 쿠폰도 UI상 "수령 완료" 상태로 전환 (재클릭 방지)
        if (result.error === "ALREADY_CLAIMED") {
          setClaimed((prev) => new Set(prev).add(coupon.id));
          toast.info(t("error.alreadyClaimed"));
          return;
        }
        const errorKey =
          result.error === "LIMIT_REACHED" ? "limitReached"
          : result.error === "EXPIRED" ? "expired"
          : "failed";
        toast.error(t(`error.${errorKey}`));
        return;
      }

      setClaimed((prev) => new Set(prev).add(coupon.id));
      toast.success(t("success"));
    } finally {
      setClaiming(null);
    }
  };

  const formatDiscount = (coupon: AvailableCoupon) => {
    // Native → selectedCurrency FX 변환 (사용자가 보는 통화로).
    // 쿠폰 금액은 스프레드 없는 market rate 사용 — face value 정합 유지.
    if (coupon.discount_type === "percentage") {
      const rate = coupon.native_discount_value;
      if (coupon.native_max_discount && ratesReady) {
        const maxConverted = roundForCurrency(
          convertCurrency(
            coupon.native_max_discount,
            coupon.native_currency,
            selectedCurrency,
            rates,
            { useMarketRate: true },
          ),
          selectedCurrency,
        );
        return `${rate}% ${t("discountMax", {
          max: formatCouponAmount(maxConverted, selectedCurrency),
        })}`;
      }
      return t("percentDiscount", { percent: rate });
    }
    // fixed: native → selectedCurrency 변환
    if (!ratesReady) {
      // FX 로드 전: native 통화로 표시 (fallback)
      return t("fixedDiscount", {
        amount: formatCouponAmount(
          coupon.native_discount_value,
          coupon.native_currency,
        ),
      });
    }
    const converted = roundForCurrency(
      convertCurrency(
        coupon.native_discount_value,
        coupon.native_currency,
        selectedCurrency,
        rates,
        { useMarketRate: true },
      ),
      selectedCurrency,
    );
    return t("fixedDiscount", {
      amount: formatCouponAmount(converted, selectedCurrency),
    });
  };

  const formatValidUntil = (dateStr: string) => {
    const d = new Date(dateStr);
    const date = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    return t("validUntil", { date });
  };

  // 로딩 중 또는 쿠폰 없을 때 렌더 안 함
  if (loading) return null;
  if (coupons.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl p-4 md:p-5 space-y-3">
      {coupons.map((coupon) => {
        const isClaimed = claimed.has(coupon.id);
        const isClaimingThis = claiming === coupon.id;
        const name = getLocalizedCouponName(coupon.translations, locale, coupon.name);
        const accent = isClaimed ? "text-gray-400" : "text-orange-600";

        return (
          <CouponTicket
            key={coupon.id}
            isClaimed={isClaimed}
            isClaimingThis={isClaimingThis}
            accent={accent}
            discount={formatDiscount(coupon)}
            name={name}
            validUntil={formatValidUntil(coupon.valid_until)}
            claimedLabel={t("claimed")}
            onClick={() => !isClaimed && !isClaimingThis && handleClaim(coupon)}
          />
        );
      })}
    </div>
  );
}

// ============================================================
// CouponTicket: 3-part 레이아웃 (좌측 + 중앙 SVG cutout + 우측)
// ============================================================

interface CouponTicketProps {
  isClaimed: boolean;
  isClaimingThis: boolean;
  accent: string;
  discount: string;
  name: string;
  validUntil: string;
  claimedLabel: string;
  onClick: () => void;
}

/**
 * 티켓 모양 쿠폰: 3-part 레이아웃
 * - 좌측: 정보 영역 (rounded-l + 우측만 열림)
 * - 중앙: 고정 크기 SVG cutout (16x80) — 반원이 납작해지지 않도록
 * - 우측: 아이콘 영역 (rounded-r + 좌측만 열림)
 *
 * 각 파트의 경계가 딱 맞으면 하나의 티켓으로 보임
 */
function CouponTicket({
  isClaimed,
  isClaimingThis,
  accent,
  discount,
  name,
  validUntil,
  claimedLabel,
  onClick,
}: CouponTicketProps) {
  const strokeColor = isClaimed ? "#D1D5DB" : "#F97316"; // orange-500
  const fillColor = isClaimed ? "#F9FAFB" : "#FFF7ED"; // orange-50
  const dashedColor = isClaimed ? "#D1D5DB" : "#FDBA74"; // orange-300

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isClaimed || isClaimingThis}
      className={cn(
        "group relative w-full h-22 flex items-stretch text-left",
        isClaimed ? "cursor-default" : "hover:opacity-90 active:opacity-80",
        "transition-opacity",
      )}
    >
      {/* 좌측: 정보 영역 */}
      <div
        className="flex-1 min-w-0 flex flex-col justify-center px-4 py-3 rounded-l-xl"
        style={{
          backgroundColor: fillColor,
          borderTop: `1.5px solid ${strokeColor}`,
          borderBottom: `1.5px solid ${strokeColor}`,
          borderLeft: `1.5px solid ${strokeColor}`,
        }}
      >
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={cn("text-base font-bold", accent)}>
            {discount}
          </span>
          <span className="text-sm font-medium text-gray-900 truncate">
            {name}
          </span>
        </div>
        <div className="mt-1 text-xs text-gray-500">
          {validUntil}
        </div>
      </div>

      {/* 중앙: cutout SVG (고정 16px 폭)
          - 완전한 원을 경계에 걸쳐 그리고 overflow:hidden으로 절반만 보이게
          - 결과: 반원 cutout(흰색) + 반원 테두리(주황)가 자연스럽게 렌더됨 */}
      <svg
        width={16}
        height={88}
        viewBox="0 0 16 88"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0"
        style={{ overflow: "hidden" }}
      >
        {/* 1. 티켓 몸통: 주황-50 */}
        <rect x={0} y={0} width={16} height={88} fill={fillColor} />

        {/* 2. 상단 cutout: 중심 y=0의 완전한 원. 위쪽 절반은 viewBox 밖으로 클리핑됨.
            fill=흰색, stroke=주황(500) → 보이는 아래쪽 절반만 반원 형태로 나타남 */}
        <circle
          cx={8}
          cy={0}
          r={8}
          fill="white"
          stroke={strokeColor}
          strokeWidth={1.5}
        />

        {/* 3. 하단 cutout: 중심 y=88의 완전한 원. 아래쪽 절반 클리핑 */}
        <circle
          cx={8}
          cy={88}
          r={8}
          fill="white"
          stroke={strokeColor}
          strokeWidth={1.5}
        />

        {/* 4. 점선 절취선 (두 cutout 사이 주황 영역) */}
        <line
          x1={8}
          y1={10}
          x2={8}
          y2={78}
          stroke={dashedColor}
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      </svg>

      {/* 우측: 아이콘 영역 */}
      <div
        className="w-20 shrink-0 flex items-center justify-center rounded-r-xl"
        style={{
          backgroundColor: fillColor,
          borderTop: `1.5px solid ${strokeColor}`,
          borderBottom: `1.5px solid ${strokeColor}`,
          borderRight: `1.5px solid ${strokeColor}`,
        }}
      >
        {isClaimingThis ? (
          <Loader2 className={cn("h-5 w-5 animate-spin", accent)} />
        ) : isClaimed ? (
          <div className="flex flex-col items-center gap-0.5">
            <Check className="h-5 w-5 text-gray-400" />
            <span className="text-[10px] text-gray-400 font-medium">
              {claimedLabel}
            </span>
          </div>
        ) : (
          <Download className="h-5 w-5 text-orange-500 group-hover:scale-110 transition-transform" />
        )}
      </div>
    </button>
  );
}
